import React from 'react';
import { UnorderedList, ListItem, Body, Box } from '@hover/blueprint';
import { StaticImage } from 'gatsby-plugin-image';
import { IMeasureRoofStep } from '../interfaces/IMeasureRoofStep';
import { IRoofingMaterial } from '../interfaces/IRoofingMaterial';
import { IMeasureRoofFAQ } from '../interfaces/IMeasureRoofFAQ';

export const MEASURE_ROOF_STEPS: Array<IMeasureRoofStep> = [
  {
    id: 1,
    heading: 'Measure roof square footage',
    isReversed: true,
    image: (
      <StaticImage
        width={538}
        placeholder="blurred"
        src="../assets/images/measure_roof_square_footage.png"
        alt="Measure roof square footage"
      />
    ),
    content: (
      <Box flexDir="column" maxW={{ base: '100%', desktop: '480px' }}>
        <Body mb={400}>
          First, you’ll need to determine the roof’s total square footage
        </Body>
        <UnorderedList ml={0}>
          <ListItem mb={400}>
            Measure the length and width of each roof surface (include dormers),
            record on a sketch of the roof
          </ListItem>
          <ListItem mb={400}>
            Note number of chimneys, skylights or other obstructions
          </ListItem>
          <ListItem mb={400}>
            Multiply length by width for the square footage of each surface (if
            a triangle, divide by 2)
          </ListItem>
          <ListItem>
            Add up the square footage for each surface to get the total square
            footage of your roof
          </ListItem>
        </UnorderedList>
      </Box>
    ),
  },
  {
    id: 2,
    heading: 'Determine roof pitch factor',
    image: (
      <StaticImage
        width={584}
        placeholder="blurred"
        src="../assets/images/determine_roof_pitch_factor.png"
        alt="Measure roof square footage"
      />
    ),
    content: (
      <Box flexDir="column">
        <Body mb={400}>
          A steeper roof needs more materials. Pitch measures roof steepness,
          boosting measurement accuracy.
        </Body>
        <UnorderedList ml={0}>
          <ListItem mb={400}>
            Measure the number of inches the roof rises vertically every 12
            inches of horizontal roof length (use a pitch app or ruler/level
            method)
          </ListItem>
          <ListItem mb={400}>
            Record as a fraction with rise over run (ex. 4/12); the larger the
            rise, the steeper the roof
          </ListItem>
          <ListItem>
            Use an online roof pitch multiplier chart to determine the roof’s
            pitch factor (a pitch of 4/12 has a pitch factor of 1.054)
          </ListItem>
        </UnorderedList>
      </Box>
    ),
  },
  {
    id: 3,
    isReversed: true,
    heading: 'Calculate Roof Squares',
    image: (
      <StaticImage
        width={584}
        placeholder="blurred"
        src="../assets/images/calculate_roof_squares.png"
        alt="Calculate roof squares"
      />
    ),
    content: (
      <Box flexDir="column" maxW={{ base: '100%', desktop: '480px' }}>
        <Body mb={400}>
          A roof square is a 10 x 10 area equal to 100 square feet of roof
          surface. To calculate:
        </Body>
        <UnorderedList ml={0}>
          <ListItem mb={400}>
            Multiply total roof square footage (Step 1) by the roof’s pitch
            factor (Step 2). Example: 2,134.0 ft&sup2; x 1.054 = 2,249.24
            ft&sup2;
          </ListItem>
          <ListItem mb={400}>
            Divide total square footage by 100. Example: 2,249.24 ft&sup2; / 100
            = 22.4924, or 22.5 roof squares
          </ListItem>
          <ListItem>
            Use the number of roof squares needed to price materials
          </ListItem>
        </UnorderedList>
      </Box>
    ),
  },
];

export const ROOFING_MATERIALS: Array<IRoofingMaterial> = [
  {
    id: 0,
    title: 'SHINGLES',
    body: 'Shingles are sold in bundles. Depending on type and brand, it may take a different number of bundles to make a roof square. When comparing shingle cost, remember to base price on squares and not on bundles.',
  },
  {
    id: 1,
    title: 'METAL ROOFING',
    body: 'Metal roofing is great for homes in snowy regions. It’s typically sold by the sheet or panel. To determine the number of sheets or panels neeeded to cover the roof, divide the width of the roof by the sheet or panel width.',
  },
  {
    id: 2,
    title: 'OTHER ROOFING MATERIALS',
    body: 'The basic roof square calculations detailed above will help estimate cost for other materials. Be sure to include the cost of leak barriers, deck protection, drip edge, rake edge, ridge vents, hip and ridge caps, etc.',
  },
];

export const MEASURE_ROOF_FAQ: Array<IMeasureRoofFAQ> = [
  {
    id: 0,
    title: 'Why is it important to get accurate roof measurements?',
    content: (
      <Body mb={500}>
        Accurate roof measurements help exterior home improvement professionals
        and homeowners more accurately estimate the quantity of required
        materials and the cost of the job. With accurate measurements,
        contractors won’t have to buy more materials mid-job (or figure out what
        to do with unused materials) – saving time and money for both pros and
        homeowners.
      </Body>
    ),
  },
  {
    id: 1,
    title: 'Is there an alternative to manually measuring my roof?',
    content: (
      <Body mb={500}>
        Yes! The HOVER app will do all the complicated work for you. Download
        the app, take some home photos, and in hours, not days, we’ll deliver
        accurate measurements to contractors and share the home’s interactive 3D
        model with homeowners and contractors. No ladders, no climbing on roofs,
        no tape measures, no math formulas needed.
      </Body>
    ),
  },
  {
    id: 2,
    title: 'Why should I use HOVER to measure my roof?',
    content: (
      <Body mb={500}>
        HOVER saves roofing professionals hours taking measurements at the job
        site and calculating quotes in the office, and it’ll save homeowners
        time in getting their project planning started. The to-scale virtual 3D
        model allows pros and homeowners to collaborate on project design and
        visualize the project before it’s built. HOVER’s accurate measurements
        eliminate manual data entry or calculations and the need to re-measure.
      </Body>
    ),
  },
  {
    id: 3,
    title: 'What are some other benefits of using HOVER?',
    content: (
      <Body mb={500}>
        HOVER makes collaboration possible. All members of the team (sales,
        production, installation, etc.) and the homeowner can be on the same
        page when it comes to the exterior home project. Resources can be
        allocated more efficiently and work can be completed on time and on
        budget.
      </Body>
    ),
  },
  {
    id: 4,
    title: 'How does HOVER work?',
    content: (
      <Body mb={500}>
        Just snap photos of the exterior of any house with the HOVER app and
        we’ll use our patented technology to calculate square footage and linear
        measurements for roof, siding, windows, trim and more. We’ll also build
        an interactive 3D model to help homeowners and pros design projects
        together. But HOVER is more than just a roofing calculator and a 3D
        model. Professionals use HOVER’s platform to estimate jobs, create
        proposals and contracts, and order directly from suppliers.
      </Body>
    ),
  },
];
